import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';
import type { InstanceRow } from './instanceModel';
import { isTerminalInterventionStatus, mergeInterventionRow } from './instanceModel';

type TranslateFn = (key: string) => string;

export interface CompletionReward {
  xp: number;
  argent: number;
  title: string;
  narrative?: string;
  multiplierInfo?: string;
}

interface VehicleActionDeps {
  authHeaders: () => Record<string, string>;
  t: TranslateFn;
  setBusyId: Dispatch<SetStateAction<number | null>>;
  setActionError: Dispatch<SetStateAction<string | null>>;
  setSectorRefreshNonce: Dispatch<SetStateAction<number>>;
  refreshInstances: () => Promise<void>;
  setInstances: Dispatch<SetStateAction<InstanceRow[]>>;
  loadInstances: () => Promise<void>;
  setSelectedVehIdsByInstance: Dispatch<SetStateAction<Record<number, number[]>>>;
  selectedVehIdsByInstance: Record<number, number[]>;
  setCompletionRewards?: Dispatch<SetStateAction<CompletionReward | null>>;
}

/**
 * Regroupe les actions véhicules de useInterventionManageController :
 * assignVehicle, advance, unassignReservedVehicle, recall*, reengage*, assignSectorVehicle.
 *
 * Extrait pour respecter la limite de 500 lignes par fichier.
 */
export function useInterventionVehicleActions(deps: VehicleActionDeps) {
  const {
    authHeaders,
    t,
    setBusyId,
    setActionError,
    setSectorRefreshNonce,
    refreshInstances,
    setInstances,
    loadInstances,
    setSelectedVehIdsByInstance,
    selectedVehIdsByInstance,
    setCompletionRewards,
  } = deps;

  const assignVehicle = useCallback(
    async (instanceId: number) => {
      const ids = selectedVehIdsByInstance[instanceId] || [];
      const uniq = ids.filter((id, i, arr) => Number.isInteger(id) && id > 0 && arr.indexOf(id) === i);
      if (uniq.length < 1) {
        setActionError(t('interventionSidebar.pickVehicleMulti'));
        return;
      }
      setBusyId(instanceId);
      setActionError(null);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/cta/appels/creer-intervention`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            appel_id: `intervention-${instanceId}`,
            vehicules: uniq.map((vehicule_id) => ({ vehicule_id, personnel_ids: [] })),
          }),
        });
        const data = await res.json();
        if (!data.success) {
          setActionError(data.error || t('interventionSidebar.assignError'));
          return;
        }
        const row = data.data?.intervention as InstanceRow | undefined;
        if (row?.id) {
          setInstances((prev) => {
            const i = prev.findIndex((x) => x.id === row.id);
            if (i === -1) return [row, ...prev];
            const next = [...prev];
            next[i] = mergeInterventionRow(next[i], row);
            return next;
          });
        } else {
          await loadInstances();
        }
        setSelectedVehIdsByInstance((p) => ({ ...p, [instanceId]: [] }));
        setSectorRefreshNonce((n) => n + 1);
        void refreshInstances();
      } catch {
        setActionError(t('interventionSidebar.assignError'));
      } finally {
        setBusyId(null);
      }
    },
    [
      selectedVehIdsByInstance,
      authHeaders,
      t,
      setInstances,
      loadInstances,
      refreshInstances,
      setActionError,
      setBusyId,
      setSectorRefreshNonce,
      setSelectedVehIdsByInstance,
    ]
  );

  const advance = useCallback(
    async (instanceId: number, outcome?: 'success' | 'failure') => {
      setBusyId(instanceId);
      setActionError(null);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/cta/interventions-instance/${instanceId}/advance`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ outcome: outcome ?? 'success' }),
        });
        const data = await res.json();
        if (!data.success) {
          setActionError(data.error || t('interventionSidebar.advanceError'));
          return;
        }
        const row = data.data as InstanceRow | undefined;
        const est =
          typeof data.travel_estimate_sec === 'number' ? data.travel_estimate_sec : undefined;
        if (row?.id) {
          setInstances((prev) =>
            prev.map((x) => {
              if (x.id !== row.id) return x;
              const merged = mergeInterventionRow(x, row);
              return est != null ? { ...merged, travel_estimate_sec: est } : merged;
            })
          );
          if (
            setCompletionRewards &&
            isTerminalInterventionStatus(row.statut || '') &&
            (outcome ?? 'success') === 'success'
          ) {
            const xp = Number(row.xp_gagne ?? 0);
            const argent = Number(row.argent_gagne ?? 0);
            const title = String(row.scenario_titre || row.numero_intervention || `#${row.id}`);
            const narrative = typeof data.narrative === 'string' && data.narrative ? data.narrative : undefined;
            const multiplierInfo = typeof data.multiplierInfo === 'string' && data.multiplierInfo ? data.multiplierInfo : undefined;
            setCompletionRewards({ xp, argent, title, narrative, multiplierInfo });
          }
        }
        setSectorRefreshNonce((n) => n + 1);
        void refreshInstances();
      } catch {
        setActionError(t('interventionSidebar.advanceError'));
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, t, setInstances, refreshInstances, setActionError, setBusyId, setSectorRefreshNonce, setCompletionRewards]
  );

  const unassignReservedVehicle = useCallback(
    async (instanceId: number, vehiculeId: number) => {
      setBusyId(instanceId);
      setActionError(null);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/cta/interventions-instance/${instanceId}/unassign-vehicle`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ vehicule_id: vehiculeId }),
        });
        const data = await res.json();
        if (!data.success) {
          setActionError(data.error || t('interventionSidebar.assignError'));
          return;
        }
        setSelectedVehIdsByInstance((p) => ({
          ...p,
          [instanceId]: (p[instanceId] || []).filter((id) => id !== vehiculeId),
        }));
        setSectorRefreshNonce((n) => n + 1);
        void refreshInstances();
      } catch {
        setActionError(t('interventionSidebar.assignError'));
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, t, refreshInstances, setActionError, setBusyId, setSectorRefreshNonce, setSelectedVehIdsByInstance]
  );

  const recallInterventionVehicles = useCallback(
    async (instanceId: number) => {
      setBusyId(instanceId);
      setActionError(null);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/cta/interventions-instance/${instanceId}/recall`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (!data.success) {
          setActionError(data.error || t('interventionSidebar.advanceError'));
          return;
        }
        setSectorRefreshNonce((n) => n + 1);
        void refreshInstances();
      } catch {
        setActionError(t('interventionSidebar.advanceError'));
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, t, refreshInstances, setActionError, setBusyId, setSectorRefreshNonce]
  );

  const recallInterventionVehicle = useCallback(
    async (instanceId: number, vehiculeId: number) => {
      setBusyId(instanceId);
      setActionError(null);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/cta/interventions-instance/${instanceId}/recall-vehicle`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ vehicule_id: vehiculeId }),
        });
        const data = await res.json();
        if (!data.success) {
          setActionError(data.error || t('interventionSidebar.advanceError'));
          return;
        }
        setSectorRefreshNonce((n) => n + 1);
        void refreshInstances();
      } catch {
        setActionError(t('interventionSidebar.advanceError'));
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, t, refreshInstances, setActionError, setBusyId, setSectorRefreshNonce]
  );

  const reengageInterventionVehicle = useCallback(
    async (instanceId: number, vehiculeId: number) => {
      setBusyId(instanceId);
      setActionError(null);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/cta/appels/creer-intervention`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            appel_id: `intervention-${instanceId}`,
            vehicules: [{ vehicule_id: vehiculeId, personnel_ids: [] }],
          }),
        });
        const data = await res.json();
        if (!data.success) {
          setActionError(data.error || t('interventionSidebar.assignError'));
          return;
        }
        setSectorRefreshNonce((n) => n + 1);
        void refreshInstances();
      } catch {
        setActionError(t('interventionSidebar.assignError'));
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, t, refreshInstances, setActionError, setBusyId, setSectorRefreshNonce]
  );

  const assignSectorVehicle = useCallback(
    async (instanceId: number, vehiculeId: number) => {
      setBusyId(instanceId);
      setActionError(null);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/cta/appels/creer-intervention`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            appel_id: `intervention-${instanceId}`,
            vehicules: [{ vehicule_id: vehiculeId, personnel_ids: [] }],
          }),
        });
        const data = await res.json();
        if (!data.success) {
          setActionError(data.error || t('interventionSidebar.assignError'));
          return;
        }
        setSelectedVehIdsByInstance((p) => ({
          ...p,
          [instanceId]: (p[instanceId] || []).filter((id) => id !== vehiculeId),
        }));
        setSectorRefreshNonce((n) => n + 1);
        void refreshInstances();
      } catch {
        setActionError(t('interventionSidebar.assignError'));
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, t, refreshInstances, setActionError, setBusyId, setSectorRefreshNonce, setSelectedVehIdsByInstance]
  );

  return {
    assignVehicle,
    advance,
    unassignReservedVehicle,
    recallInterventionVehicles,
    recallInterventionVehicle,
    reengageInterventionVehicle,
    assignSectorVehicle,
  };
}
